/**
 * Браузерная половина: папка на диске и reload служебного процесса.
 *
 * File System Access помнит каталог в IndexedDB. Первое указание — жест
 * человека. Дальше «подхватить» пишет zip и просит reload. Если API нет —
 * честная стена, не фейк «обновилось».
 */

import { AFTER_WRITE, folderOurs, newerThan, ourManifest, packOf, WALL } from '../confirm/apply';
import { reloadEnvelope } from '../confirm/reload';
import { extZipUrl } from './permit';

const DB = 'forq-ext';
const STORE = 'dir';
const KEY = 'folder';

type WritableDir = FileSystemDirectoryHandle & {
  queryPermission: (opts: { mode: 'readwrite' }) => Promise<PermissionState>;
  requestPermission: (opts: { mode: 'readwrite' }) => Promise<PermissionState>;
};

function writable(handle: FileSystemDirectoryHandle): WritableDir {
  return handle as WritableDir;
}

function picker(): ((opts: { mode: 'readwrite' }) => Promise<FileSystemDirectoryHandle>) | null {
  const fn = (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker;
  return typeof fn === 'function'
    ? (fn as (opts: { mode: 'readwrite' }) => Promise<FileSystemDirectoryHandle>).bind(window)
    : null;
}

export type ApplyOk = { ok: true; version: string; wrote: true; say: string };
export type ApplyFail = { ok: false; why: string };
export type ApplyOut = ApplyOk | ApplyFail;
export type UpdateProgress = (message: string) => void;
export type UpdateOptions = { dir?: FileSystemDirectoryHandle; progress?: UpdateProgress };
let applying = false;

function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveFolder(handle: FileSystemDirectoryHandle): Promise<void> {
  const db = await idb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(handle, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally { db.close(); }
}

export async function loadFolder(): Promise<FileSystemDirectoryHandle | null> {
  try {
    const db = await idb();
    try { return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => {
        const val = req.result;
        resolve(val instanceof FileSystemDirectoryHandle ? val : null);
      };
      req.onerror = () => reject(req.error);
      tx.onabort = () => reject(tx.error);
    }); } finally { db.close(); }
  } catch {
    return null;
  }
}

export function canWriteFolder(): boolean {
  return picker() !== null;
}

async function ensureWrite(handle: FileSystemDirectoryHandle, interactive: boolean): Promise<boolean> {
  const opts = { mode: 'readwrite' as const };
  const dir = writable(handle);
  if ((await dir.queryPermission(opts)) === 'granted') return true;
  return interactive && (await dir.requestPermission(opts)) === 'granted';
}

async function readManifest(dir: FileSystemDirectoryHandle): Promise<string | null> {
  try {
    const file = await dir.getFileHandle('manifest.json');
    return await (await file.getFile()).text();
  } catch {
    return null;
  }
}

async function writeTree(
  root: FileSystemDirectoryHandle,
  files: Map<string, Uint8Array>,
  progress: UpdateProgress = () => undefined,
): Promise<void> {
  // Manifest is written last so Chrome never observes a new version before all
  // of its scripts are present on disk.
  const ordered = [...files.entries()].sort(([a], [b]) => {
    if (a === 'manifest.json') return 1;
    if (b === 'manifest.json') return -1;
    return a.localeCompare(b);
  });
  // Validate every path before touching the folder.
  for (const [name] of ordered) {
    if (name.startsWith('/') || name.split('/').some((one) => !one || one === '.' || one === '..' || one.includes('\\'))) {
      throw new Error('архив содержит небезопасный путь');
    }
  }
  let done = 0;
  for (const [name, body] of ordered) {
    const parts = name.split('/').filter((one) => one !== '');
    if (parts.length === 0 || parts.some((one) => one === '.' || one === '..' || one.includes('\\'))) {
      throw new Error('архив содержит небезопасный путь');
    }
    const leaf = parts.pop();
    if (leaf === undefined) continue;
    let dir = root;
    for (const part of parts) {
      dir = await dir.getDirectoryHandle(part, { create: true });
    }
    const file = await dir.getFileHandle(leaf, { create: true });
    const writable = await file.createWritable();
    const copy = new Uint8Array(body.byteLength);
    copy.set(body);
    await writable.write(new Blob([copy]));
    await writable.close();
    progress(`Записываем файлы: ${++done} из ${ordered.length}…`);
  }
}

async function fileAt(root: FileSystemDirectoryHandle, name: string): Promise<{ dir: FileSystemDirectoryHandle; leaf: string }> {
  const parts = name.split('/');
  const leaf = parts.pop()!;
  let dir = root;
  for (const part of parts) dir = await dir.getDirectoryHandle(part);
  return { dir, leaf };
}

async function snapshot(root: FileSystemDirectoryHandle, files: Map<string, Uint8Array>): Promise<Map<string, Uint8Array | null>> {
  const before = new Map<string, Uint8Array | null>();
  for (const name of files.keys()) {
    if (name.startsWith('/') || name.split('/').some((part) => !part || part === '.' || part === '..' || part.includes('\\'))) {
      throw new Error('архив содержит небезопасный путь');
    }
    try {
      const { dir, leaf } = await fileAt(root, name);
      before.set(name, new Uint8Array(await (await (await dir.getFileHandle(leaf)).getFile()).arrayBuffer()));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotFoundError') before.set(name, null);
      else throw error;
    }
  }
  return before;
}

async function restore(root: FileSystemDirectoryHandle, before: Map<string, Uint8Array | null>): Promise<void> {
  await writeTree(root, new Map([...before].filter((entry): entry is [string, Uint8Array] => entry[1] !== null)));
  for (const [name, body] of before) {
    if (body !== null) continue;
    try {
      const { dir, leaf } = await fileAt(root, name);
      await dir.removeEntry(leaf);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'NotFoundError')) throw error;
    }
  }
}

const WROTE_KEY = 'forq.ext.wrote';

export function noteWrote(ver: string): void {
  try {
    sessionStorage.setItem(WROTE_KEY, ver);
  } catch {
    // Приватный режим — хватит этой вкладки.
  }
}

export function wroteThisTab(): string | null {
  try {
    return sessionStorage.getItem(WROTE_KEY);
  } catch {
    return null;
  }
}

export function clearWrote(): void {
  try { sessionStorage.removeItem(WROTE_KEY); } catch { /* Storage can be disabled. */ }
}

/** Уже писали новее, чем видит страница — не крутить запись, только reload. */
export function skipRewrite(have: string | null, wrote: string | null): boolean {
  return wrote !== null && newerThan(have, wrote);
}

export function askReload(where: Window = window): void {
  try {
    where.postMessage(reloadEnvelope(), where.origin || '*');
  } catch {
    // Старое расширение канал не слышит — ещё раз с кнопки, не uninstall.
  }
}

export function settleWrite(ms = 200): Promise<void> {
  return new Promise((ok) => setTimeout(ok, ms));
}

/** После записи на диск: подождать, пока мостик на документе сменит версию. */
export async function waitReloaded(
  want: string,
  seen: () => string | null,
  pause: (ms: number) => Promise<void> = (ms) => new Promise((ok) => setTimeout(ok, ms)),
): Promise<boolean> {
  for (let i = 0; i < 12; i += 1) {
    if (seen() === want) return true;
    await pause(250);
  }
  return false;
}

export function sayAfterReload(want: string, seen: string | null): string {
  if (seen === want) return `версия ${want} на месте`;
  return AFTER_WRITE;
}

export async function afterWrote(
  out: ApplyOut,
  seen: () => string | null,
  reload: () => void = askReload,
  wait: typeof waitReloaded = waitReloaded,
): Promise<string> {
  if (!out.ok) return out.why;
  if (await wait(out.version, seen)) return sayAfterReload(out.version, out.version);
  reload();
  const again = await wait(out.version, seen);
  return sayAfterReload(out.version, again ? out.version : seen());
}

export async function pickFolder(): Promise<FileSystemDirectoryHandle | null> {
  if (!canWriteFolder()) return null;
  try {
    const pick = picker();
    if (pick === null) return null;
    const handle = await pick({ mode: 'readwrite' });
    const text = await readManifest(handle);
    if (text === null || !folderOurs(text)) throw new Error('Выберите папку полной версии FORQ, в которой лежит manifest.json. Магазинная версия обновляется через Chrome.');
    try { await saveFolder(handle); } catch { /* Update still works when storage is unavailable. */ }
    return handle;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return null;
    throw error;
  }
}

/**
 * Скачать zip с нашего хоста, записать в указанную папку FORQ, попросить reload.
 * Чужую папку и понижение версии не пишет. Uninstall не вызывает.
 */
/** Папка уже указана и право писать живо — без нового жеста. */
export async function pullIfRemembered(have: string | null): Promise<ApplyOut> {
  return applyPack(have, false);
}

/** Запись в уже открытую папку FORQ. Не uninstall, чужие файлы в каталоге не трогает. */
export async function applyToDir(
  dir: FileSystemDirectoryHandle,
  have: string | null,
  buf: ArrayBuffer,
  reload: () => void = askReload,
  settle: () => Promise<void> = async () => undefined,
  progress: UpdateProgress = () => undefined,
): Promise<ApplyOut> {
  const existing = await readManifest(dir);
  if (existing === null || !folderOurs(existing)) {
    return { ok: false, why: 'в этой папке нет FORQ. снимать и ставить заново нельзя.' };
  }
  let pack: { version: string; files: Map<string, Uint8Array> };
  try {
    pack = await packOf(buf);
  } catch (err) {
    return { ok: false, why: err instanceof Error ? err.message : 'архив не разобрался' };
  }
  if (!newerThan(have, pack.version)) {
    return { ok: false, why: `уже стоит ${have ?? pack.version}` };
  }
  const disk = ourManifest(existing);
  if (disk !== null && newerThan(pack.version, disk.version)) {
    return { ok: false, why: `В папке уже версия ${disk.version}, архив ${pack.version} старее. Понижение отменено.` };
  }
  let before: Map<string, Uint8Array | null>;
  try {
    before = await snapshot(dir, pack.files);
  } catch {
    return { ok: false, why: 'Не удалось прочитать файлы для обновления. Проверьте папку и доступ к ней. Ничего не записано.' };
  }
  try {
    await writeTree(dir, pack.files, progress);
    progress('Проверяем записанные файлы…');
    for (const [name, expected] of pack.files) {
      const { dir: parent, leaf } = await fileAt(dir, name);
      const actual = new Uint8Array(await (await (await parent.getFileHandle(leaf)).getFile()).arrayBuffer());
      if (actual.length !== expected.length || actual.some((byte, i) => byte !== expected[i])) throw new Error('запись прервалась');
    }
  } catch {
    try {
      await restore(dir, before);
      return { ok: false, why: 'Запись обновления прервалась. Прежние файлы восстановлены; повторите попытку.' };
    } catch {
      return { ok: false, why: 'Запись прервалась, восстановить все файлы не удалось. Не перезапускайте расширение: верните доступ к папке и повторите обновление.' };
    }
  }
  noteWrote(pack.version);
  progress('Файлы записаны. Перезапускаем расширение…');
  await settle();
  reload();
  return { ok: true, version: pack.version, wrote: true, say: AFTER_WRITE };
}

async function applyPackUnlocked(
  have: string | null,
  pick: boolean,
  fetchZip: (url: string) => Promise<ArrayBuffer> = (url) =>
    fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(45000) }).then((res) => {
      if (!res.ok) throw new Error('архив не скачался');
      return res.arrayBuffer();
    }),
  options: UpdateOptions = {},
): Promise<ApplyOut> {
  if (!canWriteFolder()) return { ok: false, why: WALL };
  const progress = options.progress ?? (() => undefined);
  progress('Проверяем доступ к папке…');
  let dir = options.dir ?? await loadFolder();
  if (dir !== null) {
    try {
      if (!(await ensureWrite(dir, pick))) {
        return { ok: false, why: 'Нужен доступ к сохранённой папке. Нажмите «обновите расширение» и разрешите запись. Если папка перемещена — выберите её заново.' };
      }
    } catch {
      return { ok: false, why: 'Нет доступа к сохранённой папке. Нажмите «Выбрать другую папку» и укажите папку установленного FORQ.' };
    }
  }
  if (dir === null && pick) dir = await pickFolder();
  if (dir === null) {
    return {
      ok: false,
      why: pick
        ? 'Выбор отменён. Файлы не изменены. Нажмите «Выбрать папку» для повторной попытки.'
        : 'Выберите папку уже установленного FORQ: внутри должен лежать manifest.json. Папку запомним для следующих обновлений.',
    };
  }
  if (!pick && skipRewrite(have, wroteThisTab())) {
    const wrote = wroteThisTab();
    askReload();
    return { ok: true, version: wrote ?? have ?? '', wrote: true, say: AFTER_WRITE };
  }
  let buf: ArrayBuffer;
  progress('Скачиваем обновление…');
  try {
    buf = await fetchZip(`${extZipUrl()}?update=${encodeURIComponent(have ?? '0')}`);
  } catch {
    return { ok: false, why: 'архив не скачался. Проверьте соединение и повторите.' };
  }
  progress('Проверяем архив…');
  return applyToDir(dir, have, buf, askReload, () => settleWrite(), progress);
}

/** One writer across header/onboarding and, with Web Locks, all FORQ tabs. */
export async function applyPack(
  have: string | null,
  pick: boolean,
  fetchZip?: (url: string) => Promise<ArrayBuffer>,
  options: UpdateOptions = {},
): Promise<ApplyOut> {
  const occupied: ApplyFail = { ok: false, why: 'Обновление уже идёт в другой вкладке или окне FORQ. Дождитесь его завершения.' };
  if (applying) return occupied;
  applying = true;
  try {
    const run = (): Promise<ApplyOut> => applyPackUnlocked(have, pick, fetchZip, options);
    if (typeof navigator.locks?.request === 'function') {
      return await navigator.locks.request('forq-extension-update', { ifAvailable: true }, (lock) => lock ? run() : occupied);
    }
    return await run();
  } catch (error) {
    return { ok: false, why: error instanceof Error ? error.message : 'Обновление не завершено. Проверьте доступ к папке и повторите.' };
  } finally {
    applying = false;
  }
}
